import React, { useEffect, useState } from 'react';

/**
 * Multi-image display for the image block. Two modes:
 *  - 'slideshow': auto-fading cross-dissolve (legacy behaviour).
 *  - 'carousel' : sliding track with prev/next arrows + clickable dots, and
 *                 optional auto-advance.
 * Shared by BlockRenderer (canvas) and PublicCampaign (live) for 1:1 parity.
 */
export const ImageCarousel: React.FC<{
  images: string[];
  mode?: 'slideshow' | 'carousel';
  fit?: string;
  objectPosition?: string;
  radius?: string;
  /** auto-advance interval in seconds (0/undefined = no auto-advance). */
  speed?: number;
  /** carousel mode only: auto-advance on/off. slideshow always auto-plays. */
  autoPlay?: boolean;
  arrowColor?: string;
  dotColor?: string;
  alt?: string;
}> = ({
  images, mode = 'slideshow', fit = 'cover', objectPosition = '50% 50%',
  radius, speed = 5, autoPlay, arrowColor = '#ffffff', dotColor = '#ffffff', alt = '',
}) => {
  const [idx, setIdx] = useState(0);
  const n = images.length;
  const i = n ? ((idx % n) + n) % n : 0;
  const playing = mode === 'slideshow' || autoPlay;

  useEffect(() => {
    if (!playing || n < 2) return;
    const t = setInterval(() => setIdx(p => p + 1), Math.max(1, speed || 5) * 1000);
    return () => clearInterval(t);
  }, [playing, n, speed]);

  if (n === 0) return null;

  const imgStyle: React.CSSProperties = {
    width: '100%', height: '100%', objectFit: fit as any, objectPosition, borderRadius: radius, display: 'block',
  };

  // --- Slideshow: cross-fade ---
  if (mode === 'slideshow') {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {images.map((url, k) => (
          <img key={k} src={url} alt={alt} style={{
            ...imgStyle, position: 'absolute', inset: 0,
            opacity: k === i ? 1 : 0, transition: 'opacity 1s ease-in-out',
          }} />
        ))}
      </div>
    );
  }

  // --- Carousel: sliding track + arrows + dots ---
  const stop = (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); };
  const arrowBtn: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
    width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(0,0,0,0.35)', color: arrowColor, fontSize: 20, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* dir=ltr keeps translateX predictable regardless of page direction */}
      <div dir="ltr" style={{
        display: 'flex', width: '100%', height: '100%',
        transform: `translateX(-${i * 100}%)`, transition: 'transform .4s ease',
      }}>
        {images.map((url, k) => (
          <div key={k} style={{ flex: '0 0 100%', width: '100%', height: '100%' }}>
            <img src={url} alt={alt} style={imgStyle} />
          </div>
        ))}
      </div>
      {n > 1 && (
        <>
          <button type="button" aria-label="הקודם" onMouseDown={stop} onClick={(e) => { stop(e); setIdx(i - 1); }}
            style={{ ...arrowBtn, insetInlineStart: 8 }}>‹</button>
          <button type="button" aria-label="הבא" onMouseDown={stop} onClick={(e) => { stop(e); setIdx(i + 1); }}
            style={{ ...arrowBtn, insetInlineEnd: 8 }}>›</button>
          <div style={{ position: 'absolute', bottom: 10, insetInlineStart: 0, insetInlineEnd: 0, display: 'flex', gap: 7, justifyContent: 'center', zIndex: 2 }}>
            {images.map((_, k) => (
              <button key={k} type="button" aria-label={`תמונה ${k + 1}`} onMouseDown={stop} onClick={(e) => { stop(e); setIdx(k); }}
                style={{
                  width: 9, height: 9, padding: 0, borderRadius: '50%', cursor: 'pointer',
                  border: 'none', transition: 'opacity .2s, transform .2s',
                  background: dotColor, opacity: k === i ? 1 : 0.45,
                  transform: k === i ? 'scale(1.15)' : 'scale(1)',
                  boxShadow: '0 0 2px rgba(0,0,0,0.4)',
                }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
